import { Dispatch, Fragment, SetStateAction, useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import Button from 'components/Button';
import RejectButton from 'components/RejectButton';
import { IFlightTicketProposal } from 'entities/Flights/sdk';
import { useFlightTicketProposalExchangeRates } from 'api/ExchangeRate/sdk';
import { FlightTicketProposalStatusEnum } from 'utils/constants';
import successIcon from 'assets/success-gif-2.png';

import DeclineOfferModal from './components/DeclineOfferModal/DeclineOfferModal.tsx';
import { FlightOfferViewEnum } from '../constants';
import { KeyedMutator } from 'swr';
import { cn } from 'utils/utils';
import Skeleton from 'components/Skeleton';

const currencies = ['EUR', 'USD', 'GBP'];

const CONFIRM_BUTTON_TEXTS: Record<FlightOfferViewEnum, string> = {
  [FlightOfferViewEnum.DETAILS]: 'flightOffer.confirmAndProceedButton',
  [FlightOfferViewEnum.PAYMENT]: 'payment.proceedWithPayment',
  [FlightOfferViewEnum.COMPLETED]: '',
};

const CANCEL_BUTTON_TEXTS: Record<FlightOfferViewEnum, string> = {
  [FlightOfferViewEnum.DETAILS]: 'flightOffer.declineButton',
  [FlightOfferViewEnum.PAYMENT]: 'common.cancel',
  [FlightOfferViewEnum.COMPLETED]: '',
};

interface ITotalSectionProps {
  view: FlightOfferViewEnum;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  isConfirmDisabled: boolean;
  flightTicketProposal: IFlightTicketProposal;
  totalPriceIncludingFees?: number | null;
  handleConfirmClick: () => void;
  handleCancelClick: () => void;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
  isConfirmLoading?: boolean;
}

const TotalSection: React.FC<ITotalSectionProps> = ({
  view,
  currency,
  setCurrency,
  isConfirmDisabled,
  flightTicketProposal,
  flightTicketProposalMutate,
  totalPriceIncludingFees,
  handleConfirmClick,
  handleCancelClick,
  isConfirmLoading = false,
}) => {
  const { t } = useTranslation();
  const { offerId } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const { data: exchangeRates, isLoading: isExchangeRatesLoading } = useFlightTicketProposalExchangeRates(offerId);
  const [openMobileSection, setOpenMobileSection] = useState(false);
  const hasAlreadyAgreed = flightTicketProposal?.status === FlightTicketProposalStatusEnum.NOT_PAID;
  const toggleMobileSection = useCallback(() => {
    setOpenMobileSection((prevOpenMobileSection) => !prevOpenMobileSection);
  }, []);

  const isCurrencySelectorDisabled = flightTicketProposal.status !== FlightTicketProposalStatusEnum.PENDING;

  const selectedExchangeRate = exchangeRates?.find(
    (exchangeRate) => exchangeRate.exchange_rate.to_currency === currency
  );

  const price: number = useMemo(() => {
    if (!_.isNil(totalPriceIncludingFees)) {
      return totalPriceIncludingFees;
    }

    if (!selectedExchangeRate) {
      return Number(flightTicketProposal.price_eur);
    }

    return selectedExchangeRate?.exchanged_proposal_price.value;
  }, [totalPriceIncludingFees, selectedExchangeRate, flightTicketProposal.price_eur]);

  const onModalClose = useCallback(() => {
    setOpenModal(false);
    flightTicketProposalMutate();
  }, [flightTicketProposalMutate]);

  return (
    <Fragment>
      <div className='w-full sticky bottom-0'>
        {view !== FlightOfferViewEnum.COMPLETED && (
          <Fragment>
            {view === FlightOfferViewEnum.DETAILS && !hasAlreadyAgreed && isConfirmDisabled && (
              <div className='w-full h-[45px] bg-error100 flex justify-center items-center max-md:h-[60px]'>
                <div className='max-w-[1440px] w-full font-[MuseoSansCyrl] text-[14px] text-red px-[48px] max-lg:px-[16px]'>
                  {t('flightOffer.checkPassengerInfo')}
                </div>
              </div>
            )}
            {hasAlreadyAgreed && (
              <div className='w-full h-[45px] bg-information flex justify-center items-center max-md:h-[60px] gap-2'>
                <InformationCircleIcon className='h-6 w-6 text-white ml-10' />
                <div className='max-w-[1440px] w-full font-[MuseoSansCyrl] text-[14px] text-white '>
                  {t('flightOffer.awaitingPaymentInfo')}
                </div>
              </div>
            )}
          </Fragment>
        )}
        <div className='w-full min-h-[var(--total-section-min-height)] bg-lightBeige flex justify-center items-center max-lg:h-[84px]'>
          <div className='max-w-[1440px] w-full flex justify-between items-center px-[48px] max-lg:px-[16px]'>
            <div className='min-h-[93px]'>
              <div className='flex flex-row items-center gap-4'>
                <div className='font-[MuseoSansCyrl700] text-[32px] text-grey800 max-lg:text-[22px]'>
                  {t('flightOffer.total')}:{' '}
                  {isExchangeRatesLoading ? <Skeleton className='h-[34px] w-[123px] inline-block' /> : price.toFixed(2)}
                </div>
                <Menu as='div' className='relative inline-block'>
                  <MenuButton
                    className={cn(
                      'group inline-flex items-center gap-1 text-[32px] max-lg:text-[22px] font-medium text-grey900 focus:outline-none cursor-pointer',
                      isCurrencySelectorDisabled && 'cursor-not-allowed opacity-50'
                    )}
                    disabled={isCurrencySelectorDisabled}
                  >
                    {currency}

                    <ChevronUpIcon className='h-10 w-10 text-black group-data-[open]:hidden' />

                    <ChevronDownIcon className='hidden h-10 w-10 text-black group-data-[open]:block' />
                  </MenuButton>
                  <MenuItems className='absolute left-0 bottom-full w-21 max-h-40 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black/5'>
                    {currencies.map((item) => (
                      <MenuItem key={item}>
                        <button
                          type='button'
                          onClick={() => setCurrency(item)}
                          className='block w-full px-3 py-2 text-center text-sm data-[active]:bg-grey100'
                        >
                          {item}
                        </button>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
              <div className='font-[MuseoSansCyrl] text-[14px] text-grey800'>{t('flightOffer.taxIncluded')}</div>
              {selectedExchangeRate != null && (
                <div className='font-[MuseoSansCyrl] text-[14px] text-grey800'>
                  {t('flightOffer.usedExchangeRate')} {selectedExchangeRate.exchange_rate.from_currency} →{' '}
                  {selectedExchangeRate.exchange_rate.to_currency}: ~{selectedExchangeRate.exchange_rate.rate}
                </div>
              )}
            </div>
            {view !== FlightOfferViewEnum.COMPLETED ? (
              <div className='flex gap-[24px] max-lg:hidden'>
                <Button disabled={isConfirmDisabled} onClick={handleConfirmClick} loading={isConfirmLoading}>
                  {t(CONFIRM_BUTTON_TEXTS[view])}
                </Button>
                <RejectButton
                  onClick={view === FlightOfferViewEnum.DETAILS ? () => setOpenModal(true) : handleCancelClick}
                >
                  {t(CANCEL_BUTTON_TEXTS[view])}
                </RejectButton>
              </div>
            ) : (
              <div className='flex flex-row gap-4 items-center max-lg:hidden'>
                <p className='font-[MuseoSansCyrl] text-3xl font-bold'>{t('confirmedFlightOffer.completedOffer')}</p>
                <img src={successIcon} alt='Completed' className='w-[98px] h-[98px]' />
              </div>
            )}
            {view !== FlightOfferViewEnum.COMPLETED && (
              <div className='lg:hidden'>
                <div onClick={toggleMobileSection}>
                  {openMobileSection ? (
                    <ChevronDownIcon className='size-6 fill-grey800' />
                  ) : (
                    <ChevronUpIcon className='size-6 fill-grey800' />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {openMobileSection && view !== FlightOfferViewEnum.COMPLETED && (
          <div className='bg-beige w-full flex flex-col px-[16px] pb-[16px] gap-[12px]'>
            <RejectButton onClick={view === FlightOfferViewEnum.DETAILS ? () => setOpenModal(true) : handleCancelClick}>
              {t(CANCEL_BUTTON_TEXTS[view])}
            </RejectButton>
            <Button disabled={isConfirmDisabled} onClick={handleConfirmClick} loading={isConfirmLoading}>
              {t(CONFIRM_BUTTON_TEXTS[view])}
            </Button>
          </div>
        )}
        <div className='lg:hidden max-lg:flex max-lg:flex-row max-lg:p-4 gap-4 items-center justify-between'>
          <p className='font-[MuseoSansCyrl] text-3xl max-lg:text-xl font-bold'>
            {t('confirmedFlightOffer.completedOffer')}
          </p>
          <img src={successIcon} alt='Completed' className='w-[64px] h-[64px]' />
        </div>
      </div>
      {offerId && <DeclineOfferModal open={openModal} offerId={offerId} onClose={onModalClose} />}
    </Fragment>
  );
};
export default TotalSection;
